import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { APIStorage } from '@/services/apiStorage';
import { Check } from 'lucide-react';

interface MFASetupProps {
  userEmail: string;
  onComplete: (token: string) => void;
}

type Step = 1 | 2 | 3;

const STEP_LABELS: Record<Step, string> = {
  1: 'TOTP registrieren',
  2: 'Recovery E-Mail',
  3: 'Recovery Codes',
};

function StepIndicator({ currentStep }: { currentStep: Step }) {
  const steps: Step[] = [1, 2, 3];

  return (
    <div className="flex items-center justify-center gap-0 mb-8" role="list" aria-label="Fortschritt">
      {steps.map((step, index) => {
        const isCompleted = step < currentStep;
        const isActive = step === currentStep;
        const isUpcoming = step > currentStep;

        return (
          <div key={step} className="flex items-center" role="listitem">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors',
                  isCompleted && 'border-primary bg-primary text-primary-foreground',
                  isActive && 'border-primary bg-primary text-primary-foreground',
                  isUpcoming && 'border-muted-foreground/40 text-muted-foreground',
                )}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`Schritt ${step}: ${STEP_LABELS[step]}${isCompleted ? ' (abgeschlossen)' : isActive ? ' (aktuell)' : ''}`}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : step}
              </div>
              <span
                className={cn(
                  'mt-1.5 text-xs whitespace-nowrap',
                  isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {STEP_LABELS[step]}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'mx-2 mt-[-1.25rem] h-0.5 w-12',
                  step < currentStep ? 'bg-primary' : 'bg-muted-foreground/30',
                )}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function MFASetup({ userEmail, onComplete }: MFASetupProps) {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [error, setError] = useState<string | null>(null);

  // Step 1 state
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Step 2 state
  const [email, setEmail] = useState(userEmail);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);

  // Step 3 state
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codesCopied, setCodesCopied] = useState(false);
  const [codesConfirmed, setCodesConfirmed] = useState(false);
  const [completing, setCompleting] = useState(false);

  const apiRef = useRef(new APIStorage());

  // Auto-initiate TOTP setup on mount
  useEffect(() => {
    let cancelled = false;

    async function initiate() {
      setInitiating(true);
      setError(null);
      try {
        const result = await apiRef.current.mfaSetupInitiate();
        if (!cancelled) {
          setDeviceId(result.deviceId);
          setSecret(result.secret);
          setQrDataUrl(result.qrDataUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim Initialisieren der MFA-Einrichtung.',
          );
        }
      } finally {
        if (!cancelled) {
          setInitiating(false);
        }
      }
    }

    void initiate();
    return () => { cancelled = true; };
  }, []);

  // Auto-generate recovery codes when entering step 3
  useEffect(() => {
    if (currentStep !== 3 || recoveryCodes.length > 0 || loadingCodes) return;
    let cancelled = false;

    async function generate() {
      setLoadingCodes(true);
      setError(null);
      try {
        const result = await apiRef.current.mfaSetupGenerateCodes();
        if (!cancelled) {
          setRecoveryCodes(result.codes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Fehler beim Generieren der Recovery Codes.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingCodes(false);
        }
      }
    }

    void generate();
    return () => { cancelled = true; };
  }, [currentStep, recoveryCodes.length, loadingCodes]);

  const retryInitiate = useCallback(async () => {
    setInitiating(true);
    setError(null);
    try {
      const result = await apiRef.current.mfaSetupInitiate();
      setDeviceId(result.deviceId);
      setSecret(result.secret);
      setQrDataUrl(result.qrDataUrl);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Fehler beim Initialisieren der MFA-Einrichtung.',
      );
    } finally {
      setInitiating(false);
    }
  }, []);

  const verifyTotp = useCallback(async () => {
    if (!deviceId || totpCode.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      const result = await apiRef.current.mfaSetupVerifyTotp(deviceId, totpCode);
      if (result.verified) {
        setCurrentStep(2);
        setError(null);
      } else {
        setError('Der Code ist ungueltig. Bitte versuche es erneut.');
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Fehler bei der Code-Verifizierung.',
      );
    } finally {
      setVerifying(false);
    }
  }, [deviceId, totpCode]);

  const sendRecoveryEmailOtp = useCallback(async () => {
    if (!email.trim()) {
      setError('Bitte eine E-Mail-Adresse eingeben.');
      return;
    }
    setSendingOtp(true);
    setOtpError(null);
    setError(null);
    try {
      const result = await apiRef.current.mfaSetupVerifyRecoveryEmail(email.trim());
      setMaskedEmail(result.maskedEmail);
      setEmailOtpSent(true);
      setEmailOtpCode('');
      if (result.dryRun) {
        // Email-Service nicht konfiguriert: Backend hat die Recovery-Email
        // bereits im verify-recovery-email-Handler persistiert — direkt weiter.
        setCurrentStep(3);
      }
    } catch (err) {
      setOtpError(
        err instanceof Error ? err.message : 'Fehler beim Senden des Codes.',
      );
    } finally {
      setSendingOtp(false);
    }
  }, [email]);

  const confirmRecoveryEmail = useCallback(async () => {
    if (emailOtpCode.length !== 6) return;
    setVerifyingOtp(true);
    setOtpError(null);
    try {
      const result = await apiRef.current.mfaSetupConfirmRecoveryEmail(emailOtpCode);
      if (result.confirmed) {
        setCurrentStep(3);
        setError(null);
      } else {
        setOtpError('Ungültiger Code. Bitte versuche es erneut.');
      }
    } catch (err) {
      setOtpError(
        err instanceof Error ? err.message : 'Fehler bei der Verifizierung.',
      );
    } finally {
      setVerifyingOtp(false);
    }
  }, [emailOtpCode]);

  const retryGenerateCodes = useCallback(async () => {
    setLoadingCodes(true);
    setError(null);
    try {
      const result = await apiRef.current.mfaSetupGenerateCodes();
      setRecoveryCodes(result.codes);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Fehler beim Generieren der Recovery Codes.',
      );
    } finally {
      setLoadingCodes(false);
    }
  }, []);

  const copyCodes = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCodesCopied(true);
      setTimeout(() => setCodesCopied(false), 3000);
    } catch {
      setError('Codes konnten nicht in die Zwischenablage kopiert werden.');
    }
  }, [recoveryCodes]);

  const completeSetup = useCallback(async () => {
    if (!deviceId) return;
    setCompleting(true);
    setError(null);
    try {
      const result = await apiRef.current.mfaSetupComplete(deviceId);
      if (result.success) {
        onComplete(result.token);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Fehler beim Abschliessen der MFA-Einrichtung.',
      );
    } finally {
      setCompleting(false);
    }
  }, [deviceId, onComplete]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold tracking-tight">
            Zwei-Faktor-Authentifizierung einrichten
          </CardTitle>
          <CardDescription>
            Schuetze dein Konto mit einem zusaetzlichen Sicherheitsfaktor.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StepIndicator currentStep={currentStep} />

          {error && (
            <div
              className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
              role="alert"
            >
              {error}
            </div>
          )}

          {currentStep === 1 && (
            <StepTotp
              initiating={initiating}
              qrDataUrl={qrDataUrl}
              secret={secret}
              showManualEntry={showManualEntry}
              onToggleManualEntry={() => setShowManualEntry(!showManualEntry)}
              totpCode={totpCode}
              onTotpCodeChange={setTotpCode}
              onVerify={verifyTotp}
              verifying={verifying}
              onRetry={retryInitiate}
            />
          )}
          {currentStep === 2 && (
            <StepRecoveryEmail
              email={email}
              onEmailChange={setEmail}
              otpSent={emailOtpSent}
              otpCode={emailOtpCode}
              onOtpCodeChange={setEmailOtpCode}
              onSendOtp={sendRecoveryEmailOtp}
              onVerifyOtp={confirmRecoveryEmail}
              sendingOtp={sendingOtp}
              verifyingOtp={verifyingOtp}
              maskedEmail={maskedEmail}
              otpError={otpError}
            />
          )}
          {currentStep === 3 && (
            <StepRecoveryCodes
              loading={loadingCodes}
              codes={recoveryCodes}
              codesCopied={codesCopied}
              codesConfirmed={codesConfirmed}
              completing={completing}
              onCopy={copyCodes}
              onConfirmChange={setCodesConfirmed}
              onComplete={completeSetup}
              onRetry={retryGenerateCodes}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Step sub-components ---

interface StepTotpProps {
  initiating: boolean;
  qrDataUrl: string | null;
  secret: string | null;
  showManualEntry: boolean;
  onToggleManualEntry: () => void;
  totpCode: string;
  onTotpCodeChange: (code: string) => void;
  onVerify: () => void;
  verifying: boolean;
  onRetry: () => void;
}

function StepTotp({
  initiating,
  qrDataUrl,
  secret,
  showManualEntry,
  onToggleManualEntry,
  totpCode,
  onTotpCodeChange,
  onVerify,
  verifying,
  onRetry,
}: StepTotpProps) {
  if (initiating) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">
          Authenticator wird vorbereitet...
        </p>
      </div>
    );
  }

  if (!qrDataUrl || !secret) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <p className="text-sm text-muted-foreground">
          Einrichtung konnte nicht gestartet werden.
        </p>
        <Button onClick={onRetry} variant="outline">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Scanne den QR-Code mit deiner Authenticator-App (z.B. Google
          Authenticator, Authy, Microsoft Authenticator).
        </p>
        <div className="flex justify-center rounded-lg border bg-white p-4 dark:bg-white">
          <img
            src={qrDataUrl}
            alt="QR-Code fuer Authenticator-App"
            className="h-48 w-48"
          />
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={onToggleManualEntry}
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {showManualEntry ? 'QR-Code anzeigen' : 'Manuell eingeben'}
        </button>
        {showManualEntry && (
          <div className="mt-3 rounded-md border bg-muted/50 p-3">
            <p className="mb-1 text-xs text-muted-foreground">
              Geheimschluessel:
            </p>
            <code className="block break-all font-mono text-sm font-medium tracking-wider">
              {secret}
            </code>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="totp-code">Verifizierungscode</Label>
        <Input
          id="totp-code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          value={totpCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            onTotpCodeChange(value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && totpCode.length === 6) {
              onVerify();
            }
          }}
          className="font-mono text-center text-lg tracking-[0.5em]"
          autoComplete="one-time-code"
          aria-describedby="totp-hint"
        />
        <p id="totp-hint" className="text-xs text-muted-foreground">
          Gib den 6-stelligen Code aus deiner Authenticator-App ein.
        </p>
      </div>

      <Button
        onClick={onVerify}
        disabled={totpCode.length !== 6 || verifying}
        className="w-full"
      >
        {verifying ? 'Wird verifiziert...' : 'Code verifizieren'}
      </Button>
    </div>
  );
}

interface StepRecoveryEmailProps {
  email: string;
  onEmailChange: (email: string) => void;
  otpSent: boolean;
  otpCode: string;
  onOtpCodeChange: (code: string) => void;
  onSendOtp: () => void;
  onVerifyOtp: () => void;
  sendingOtp: boolean;
  verifyingOtp: boolean;
  maskedEmail: string | null;
  otpError: string | null;
}

function StepRecoveryEmail({
  email,
  onEmailChange,
  otpSent,
  otpCode,
  onOtpCodeChange,
  onSendOtp,
  onVerifyOtp,
  sendingOtp,
  verifyingOtp,
  maskedEmail,
  otpError,
}: StepRecoveryEmailProps) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Hinterlege eine E-Mail-Adresse fuer die Kontowiederherstellung. An diese
        Adresse werden Verifizierungscodes gesendet, falls du den Zugang zu deiner
        Authenticator-App verlierst.
      </p>

      <div className="space-y-2">
        <Label htmlFor="recovery-email">Recovery E-Mail</Label>
        <Input
          id="recovery-email"
          type="email"
          placeholder="name@beispiel.de"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !otpSent) {
              onSendOtp();
            }
          }}
          disabled={otpSent}
          aria-describedby="email-hint"
        />
        <p id="email-hint" className="text-xs text-muted-foreground">
          Verwende eine E-Mail-Adresse, auf die du auch ohne dieses Konto
          zugreifen kannst.
        </p>
      </div>

      {!otpSent ? (
        <Button
          onClick={onSendOtp}
          disabled={!email.trim() || sendingOtp}
          className="w-full"
        >
          {sendingOtp ? 'Code wird gesendet...' : 'Bestaetigungscode senden'}
        </Button>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Code gesendet an <span className="font-medium text-foreground">{maskedEmail}</span>
          </p>

          <div className="space-y-2">
            <Label htmlFor="otp-code">Bestaetigungscode</Label>
            <Input
              id="otp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                onOtpCodeChange(value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && otpCode.length === 6) {
                  onVerifyOtp();
                }
              }}
              className="font-mono text-center text-lg tracking-[0.5em]"
              autoComplete="one-time-code"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Der Code ist 5 Minuten gueltig.
            </p>
          </div>

          {otpError && (
            <p className="text-sm text-red-600 dark:text-red-400 text-center">
              {otpError}
            </p>
          )}

          <Button
            onClick={onVerifyOtp}
            disabled={otpCode.length !== 6 || verifyingOtp}
            className="w-full"
          >
            {verifyingOtp ? 'Wird verifiziert...' : 'E-Mail bestaetigen'}
          </Button>

          <button
            type="button"
            onClick={onSendOtp}
            disabled={sendingOtp}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors block mx-auto disabled:opacity-50"
          >
            Neuen Code senden
          </button>
        </div>
      )}
    </div>
  );
}

interface StepRecoveryCodesProps {
  loading: boolean;
  codes: string[];
  codesCopied: boolean;
  codesConfirmed: boolean;
  completing: boolean;
  onCopy: () => void;
  onConfirmChange: (confirmed: boolean) => void;
  onComplete: () => void;
  onRetry: () => void;
}

function StepRecoveryCodes({
  loading,
  codes,
  codesCopied,
  codesConfirmed,
  completing,
  onCopy,
  onConfirmChange,
  onComplete,
  onRetry,
}: StepRecoveryCodesProps) {
  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">
          Recovery Codes werden generiert...
        </p>
      </div>
    );
  }

  if (codes.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <p className="text-sm text-muted-foreground">
          Codes konnten nicht generiert werden.
        </p>
        <Button onClick={onRetry} variant="outline">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Speichere diese Recovery Codes an einem sicheren Ort. Jeder Code kann
        nur einmal verwendet werden, um dich anzumelden, falls du keinen Zugriff
        auf deine Authenticator-App hast.
      </p>

      <div
        className="rounded-md border bg-muted/50 p-4"
        role="region"
        aria-label="Recovery Codes"
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          {codes.map((code, index) => (
            <code
              key={index}
              className="font-mono text-sm tracking-wider tabular-nums"
            >
              {code}
            </code>
          ))}
        </div>
      </div>

      <Button onClick={onCopy} variant="outline" className="w-full">
        {codesCopied ? 'Kopiert!' : 'Codes kopieren'}
      </Button>

      <div className="flex items-start gap-3">
        <Checkbox
          id="codes-confirmed"
          checked={codesConfirmed}
          onCheckedChange={(checked) => onConfirmChange(checked === true)}
        />
        <Label
          htmlFor="codes-confirmed"
          className="text-sm leading-relaxed cursor-pointer"
        >
          Ich habe die Codes sicher gespeichert
        </Label>
      </div>

      <Button
        onClick={onComplete}
        disabled={!codesConfirmed || completing}
        className="w-full"
      >
        {completing ? 'Wird abgeschlossen...' : 'Abschliessen'}
      </Button>
    </div>
  );
}
